import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@medprompt/shared';
import '@medprompt/shared/src/auth/LoginView.css';

const SignUpViewController = () => {
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { signup } = useAuth();

  // Get isReset flag from location state
  const isReset = location.state?.isReset || false;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!email || !verificationCode || !password || !confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(`Password must contain ${passwordErrors.join(', ')}`);
      return;
    }

    if (verificationCode.length !== 6 || !/^\d+$/.test(verificationCode)) {
      setError(`Please enter a valid 6-digit ${isReset ? 'reset' : 'verification'} code`);
      return;
    }
    
    const normalizedEmail = email.toLowerCase();

    try {
      setLoading(true);
      setError('');
      
      const result = await signup(normalizedEmail, verificationCode, password);
      
      // After successful signup/reset, redirect to verification page
      navigate('/verify', { 
        state: { 
          email: normalizedEmail,
          from: { pathname: '/' }
        } 
      });
      
    } catch (error) {
      console.error(`${isReset ? 'Password reset' : 'Signup'} failed:`, error);
      setError(`${isReset ? 'Password reset' : 'Signup'} failed: ` + (error.message || 'Please check your information and try again'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/login');
  };

  // Validate password strength
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('at least 8 characters');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('an uppercase letter');
    }
    
    if (!/[0-9]/.test(password)) {
      errors.push('a number');
    }
    
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('a special character');
    }
    
    return errors;
  };

  // Validate if the verification code input is a 6-digit string
  const handleCodeChange = (e) => {
    const value = e.target.value;
    // Only allow digits and max length of 6
    if (value === '' || (/^\d+$/.test(value) && value.length <= 6)) {
      setVerificationCode(value);
    }
  };

  return (
    <div className="login-container">
      <div className="login-form-container">
        <div className="login-header">
          <h1>{isReset ? 'Reset Password' : 'Create Account'}</h1>
          <p>{isReset ? 'Enter your new password with your reset code' : 'Sign up with your invitation'}</p>
        </div>
        
        {error && <div className="login-error">{error}</div>}
        
        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              disabled={loading}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="verificationCode">{isReset ? 'Reset Code' : 'Invitation Code'}</label>
            <input
              type="text"
              id="verificationCode"
              value={verificationCode}
              onChange={handleCodeChange}
              placeholder="Enter 6-digit code"
              disabled={loading}
              maxLength={6}
              required
            />
            <small>Enter the 6-digit code from your {isReset ? 'reset' : 'invitation'} email</small>
          </div>
          
          <div className="form-group">
            <label htmlFor="password">{isReset ? 'New Password' : 'Password'}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isReset ? 'Enter your new password' : 'Create a password'}
              disabled={loading}
              required
            />
            <small>Must be at least 8 characters with an uppercase letter, number, and special character</small>
          </div>
          
          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              disabled={loading}
              required
            />
          </div>
          
          <button type="submit" className="login-button" disabled={loading}>
            {loading ? (isReset ? 'Resetting Password...' : 'Creating Account...') : (isReset ? 'Reset Password' : 'Sign Up')}
          </button>
          
          <button type="button" className="back-button" onClick={handleBack} disabled={loading}>
            Back to Login
          </button>
        </form>
      </div>
    </div>
  );
};

export default SignUpViewController; 